import React, { useState, useRef, useEffect } from 'react';
import { TextField, Grid } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormContainer from '../../components/FormContainer';
import { addConstituency, updateConstituency } from '../../utils/api';

const ConstituencyForm = ({ editConstituency = null, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const isEditMode = !!editConstituency;
  const onSuccessTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (onSuccessTimerRef.current != null) {
        clearTimeout(onSuccessTimerRef.current);
        onSuccessTimerRef.current = null;
      }
    },
    []
  );

  const validationSchema = Yup.object({
    lokSabhaConstituency: Yup.string().required('Lok Sabha Constituency is required').max(100, 'Must be at most 100 characters'),
    rajyaSabhaConstituency: Yup.string().required('Rajya Sabha Constituency is required').max(100, 'Must be at most 100 characters'),
    vidhanSabhaConstituency: Yup.string().required('Vidhan Sabha Constituency is required').max(100, 'Must be at most 100 characters'),
    pinCode: Yup.string()
      .required('PIN Code is required')
      .matches(/^[0-9]{6}$/, 'PIN Code must be exactly 6 digits'),
  });

  const formik = useFormik({
    initialValues: {
      lokSabhaConstituency: editConstituency ? editConstituency.lokSabhaConstituency : '',
      rajyaSabhaConstituency: editConstituency ? editConstituency.rajyaSabhaConstituency : '',
      vidhanSabhaConstituency: editConstituency ? editConstituency.vidhanSabhaConstituency : '',
      pinCode: editConstituency ? editConstituency.pinCode : '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      setSuccessMessage(null);
      setErrorMessage(null);

      try {
        if (isEditMode) {
          await updateConstituency(editConstituency.constituencyId, values, {
            previousData: editConstituency,
            identifier:
              editConstituency.lokSabhaConstituency || values.lokSabhaConstituency
          });
          setSuccessMessage('Constituency updated successfully!');
        } else {
          await addConstituency(values, {
            identifier: values.lokSabhaConstituency
          });
          setSuccessMessage('Constituency added successfully!');
          resetForm();
        }
        
        if (onSuccess) {
          if (onSuccessTimerRef.current != null) clearTimeout(onSuccessTimerRef.current);
          onSuccessTimerRef.current = setTimeout(() => {
            onSuccessTimerRef.current = null;
            onSuccess();
          }, 1500);
        }
      } catch (error) {
        console.error(`Error ${isEditMode ? 'updating' : 'adding'} constituency:`, error);
        setErrorMessage(error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'add'} constituency. Please try again.`);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  return (
    <FormContainer
      title={isEditMode ? "Edit Constituency" : "Add Constituency"}
      onSubmit={formik.handleSubmit}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={errorMessage}
      onCloseSnackbar={handleCloseSnackbar}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            id="lokSabhaConstituency"
            name="lokSabhaConstituency"
            label="Lok Sabha Constituency"
            value={formik.values.lokSabhaConstituency}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.lokSabhaConstituency && Boolean(formik.errors.lokSabhaConstituency)}
            helperText={formik.touched.lokSabhaConstituency && formik.errors.lokSabhaConstituency}
            margin="normal"
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            id="rajyaSabhaConstituency"
            name="rajyaSabhaConstituency"
            label="Rajya Sabha Constituency"
            value={formik.values.rajyaSabhaConstituency}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.rajyaSabhaConstituency && Boolean(formik.errors.rajyaSabhaConstituency)}
            helperText={formik.touched.rajyaSabhaConstituency && formik.errors.rajyaSabhaConstituency}
            margin="normal"
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            id="vidhanSabhaConstituency"
            name="vidhanSabhaConstituency"
            label="Vidhan Sabha Constituency"
            value={formik.values.vidhanSabhaConstituency}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.vidhanSabhaConstituency && Boolean(formik.errors.vidhanSabhaConstituency)}
            helperText={formik.touched.vidhanSabhaConstituency && formik.errors.vidhanSabhaConstituency}
            margin="normal"
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            id="pinCode"
            name="pinCode"
            label="PIN Code"
            value={formik.values.pinCode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.pinCode && Boolean(formik.errors.pinCode)}
            helperText={formik.touched.pinCode && formik.errors.pinCode}
            margin="normal"
            variant="outlined"
            inputProps={{ maxLength: 6 }}
          />
        </Grid>
      </Grid>
    </FormContainer>
  );
};

export default ConstituencyForm;

